import { Alerta, Button, Card, Casilla, DataList, Icono, Spinner, Stack, TextField } from "@/components";
import type { DispositivoPush } from "@/lib/api";
import { formatFechaLocal } from "@/lib/format";
import { usePerfilTab } from "./usePerfilTab";

export function PerfilTab() {
  const t = usePerfilTab();
  return (
    <Stack espacio={6}>
      <h1 className="text-pantalla font-light tracking-[-0.03em]">Mi perfil</h1>
      <Card>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
          <dt className="text-texto-suave">Nombre</dt>
          <dd>{t.nombre}</dd>
          <dt className="text-texto-suave">Email</dt>
          <dd className="break-all">{t.email}</dd>
          <dt className="text-texto-suave">Rol</dt>
          <dd>{t.rol}</dd>
        </dl>
      </Card>
      {t.mostrarAvisos && (
        <Card>
          <Stack espacio={4}>
            <h2 className="text-titulo font-light tracking-tight">Avisos</h2>
            <p className="text-chico text-texto-suave">Se guardan al tocarlos.</p>
            {t.prefs && (
              <div className="grid gap-4 sm:grid-cols-2">
                <GrupoPreferencias
                  titulo="Por mail"
                  detalle={`A ${t.email}`}
                  llamados={t.prefs.avisar_llamados}
                  novedades={t.prefs.avisar_novedades}
                  onLlamados={(v) => void t.cambiarPreferencia("avisar_llamados", v)}
                  onNovedades={(v) => void t.cambiarPreferencia("avisar_novedades", v)}
                />
                <GrupoPreferencias
                  titulo="En el celular"
                  detalle="En los celulares donde actives los avisos"
                  llamados={t.prefs.push_llamados}
                  novedades={t.prefs.push_novedades}
                  onLlamados={(v) => void t.cambiarPreferencia("push_llamados", v)}
                  onNovedades={(v) => void t.cambiarPreferencia("push_novedades", v)}
                />
              </div>
            )}
            <Alerta texto={t.errorPrefs} />
          </Stack>
        </Card>
      )}
      {t.mostrarAvisos && (
        <Card>
          <Stack espacio={3}>
            <h2 className="text-titulo font-light tracking-tight">Este celular</h2>
            <EsteCelular t={t} />
            <Alerta texto={t.errorCelular} />
            {t.dispositivos.length > 0 && (
              <>
                <h3 className="font-semibold">Tus dispositivos</h3>
                <DataList
                  etiqueta="Tus dispositivos"
                  columnas={[
                    { clave: "nombre", titulo: "Dispositivo", render: (d: DispositivoPush) => d.nombre },
                    { clave: "desde", titulo: "Desde", render: (d: DispositivoPush) => formatFechaLocal(d.creado_en) },
                  ]}
                  filas={t.dispositivos}
                  claveFila={(d) => d.id}
                  acciones={(d) => (
                    <Button variante="fantasma" tamanio="sm" aria-label={`Quitar ${d.nombre}`} onClick={() => void t.quitarDispositivo(d)}>
                      Quitar
                    </Button>
                  )}
                />
              </>
            )}
          </Stack>
        </Card>
      )}
      <Card>
        <form onSubmit={(e) => void t.enviar(e)} noValidate>
          <Stack espacio={4}>
            <h2 className="text-titulo font-light tracking-tight">Cambiar contraseña</h2>
            <TextField etiqueta="Contraseña actual" type="password" autoComplete="current-password" value={t.actual} onChange={(e) => t.setActual(e.target.value)} />
            <TextField etiqueta="Contraseña nueva" type="password" autoComplete="new-password" value={t.nueva} onChange={(e) => t.setNueva(e.target.value)} />
            <TextField etiqueta="Repetí la contraseña nueva" type="password" autoComplete="new-password" value={t.repetida} onChange={(e) => t.setRepetida(e.target.value)} />
            <Alerta texto={t.error} />
            {t.exito && (
              <p role="status" className="rounded-control bg-exito-suave px-3 py-2 text-chico text-exito">
                Listo, tu contraseña quedó cambiada. Cerramos tus otras sesiones abiertas.
              </p>
            )}
            <div>
              <Button type="submit" cargando={t.guardando}>
                Cambiar contraseña
              </Button>
            </div>
          </Stack>
        </form>
      </Card>
    </Stack>
  );
}

function GrupoPreferencias(p: {
  titulo: string;
  detalle: string;
  llamados: boolean;
  novedades: boolean;
  onLlamados: (v: boolean) => void;
  onNovedades: (v: boolean) => void;
}) {
  return (
    <div role="group" aria-label={p.titulo}>
      <Stack espacio={2}>
        <h3 className="font-semibold">{p.titulo}</h3>
        <p className="text-chico text-texto-suave break-all">{p.detalle}</p>
        <Casilla etiqueta="Previsiones de gastos" ayuda="Cuando se emite y 3 días antes de que venza, si todavía debés." marcado={p.llamados} onCambio={p.onLlamados} />
        <Casilla etiqueta="Novedades de obra" ayuda="Cuando se publica una novedad del avance." marcado={p.novedades} onCambio={p.onNovedades} />
      </Stack>
    </div>
  );
}

function EsteCelular({ t }: { t: ReturnType<typeof usePerfilTab> }) {
  switch (t.estadoCelular) {
    case null:
      return <Spinner />;
    case "ios-sin-instalar":
      return (
        <Stack espacio={2}>
          <p>Para recibir avisos en el iPhone, instalá la app:</p>
          <ol className="list-decimal space-y-2 pl-5">
            <li>
              Tocá <Icono nombre="compartir" className="inline size-5 align-text-bottom" /> Compartir, abajo en Safari.
            </li>
            <li>
              Elegí <Icono nombre="agregar" className="inline size-5 align-text-bottom" /> “Agregar a pantalla de inicio”.
            </li>
            <li>Abrí 2M desde el ícono nuevo.</li>
            <li>Volvé a Mi perfil y tocá “Activar avisos en este celular”.</li>
          </ol>
        </Stack>
      );
    case "sin-soporte":
      return <p>Este navegador no recibe avisos; te seguimos avisando por mail.</p>;
    case "no-habilitado":
      return <p>Los avisos al celular todavía no están habilitados.</p>;
    case "bloqueado":
      return (
        <p>
          Bloqueaste los avisos para esta app. Para habilitarlos: en iPhone, Ajustes → Notificaciones → 2M → Permitir notificaciones. En
          Android, mantené apretado el ícono → Información de la app → Notificaciones.
        </p>
      );
    case "disponible":
      return (
        <div>
          <Button cargando={t.ocupadoCelular} onClick={() => void t.activarCelular()}>
            Activar avisos en este celular
          </Button>
        </div>
      );
    case "activado":
      return (
        <Stack espacio={2}>
          <p>Los avisos están activados en este celular.</p>
          <div>
            <Button variante="secundario" cargando={t.ocupadoCelular} onClick={() => void t.desactivarCelular()}>
              Desactivar
            </Button>
          </div>
        </Stack>
      );
  }
}
